import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

interface SkinnerBoxSparklesProps {
  imgStar: string;
  play?: boolean;
  isLooping?: boolean;
  durationMs?: number;
  onComplete?: () => void;
  direction?: "out" | "in";
  zIndex?: number;
}

const SPARKLES_CONFIG = {
  STAR_SIZE_PX: 40,
  CIRCLE_DIAMETER_PX: 80,
  START_OFFSET_FROM_BORDER_PX: 60,
  SPREAD_RADIUS_PX: 100,
  TOTAL_STARS: 21,
  DEFAULT_DURATION_MS: 1500,
  ROTATION_MIN: 10,
  ROTATION_MAX: 30,
  BATCH_DELAY_MS: 300, // Delay between batches
  TOTAL_BATCHES: 3, // Number of batches
  SEQUENCE_PAUSE_MS: 100, // Pause after finishing all batches
} as const;

const SkinnerBoxSparkles = ({
  imgStar,
  play = true,
  isLooping = true,
  durationMs = SPARKLES_CONFIG.DEFAULT_DURATION_MS,
  onComplete,
  direction = "out",
  zIndex,
}: SkinnerBoxSparklesProps) => {
  const CIRCLE_RADIUS_PX = SPARKLES_CONFIG.CIRCLE_DIAMETER_PX / 2;

  const doneRef = useRef(false);
  const [sequenceCycle, setSequenceCycle] = useState(0);

  // Total time for one full sequence (all batches) to finish
  const sequenceDurationMs = (SPARKLES_CONFIG.TOTAL_BATCHES - 1) * SPARKLES_CONFIG.BATCH_DELAY_MS + durationMs;

  useEffect(() => {
    if (!play) return;
    if (isLooping) return;
    doneRef.current = false;
    const timer = setTimeout(() => {
      if (!doneRef.current) {
        doneRef.current = true;
        onComplete?.();
      }
    }, sequenceDurationMs);
    return () => {
      clearTimeout(timer);
      doneRef.current = true;
    };
  }, [play, isLooping, sequenceDurationMs, onComplete]);

  // Loop entire sequence: 3 throws then pause, repeat
  useEffect(() => {
    if (!play) return;
    if (!isLooping) return;
    const totalWait = sequenceDurationMs + SPARKLES_CONFIG.SEQUENCE_PAUSE_MS;
    const timer = setTimeout(() => {
      setSequenceCycle((c) => c + 1);
    }, totalWait);
    return () => clearTimeout(timer);
  }, [play, isLooping, sequenceDurationMs, sequenceCycle]);

  if (!play) return null;

  const Sparkle = ({ batchIndex }: { batchIndex: number }) => {
    const generateParams = () => {
      // Uniformly distribute angle so stars start around the circle
      const angle = Math.random() * Math.PI * 2;
      const size = SPARKLES_CONFIG.STAR_SIZE_PX;
      const halfSize = size / 2;
      const startRadiusPx = Math.max(0, CIRCLE_RADIUS_PX - SPARKLES_CONFIG.START_OFFSET_FROM_BORDER_PX);
      const endRadiusPx = startRadiusPx + SPARKLES_CONFIG.SPREAD_RADIUS_PX;
      // Subtract half size so the image center aligns with the polar coordinates
      const x0 = Math.cos(angle) * startRadiusPx - halfSize;
      const y0 = Math.sin(angle) * startRadiusPx - halfSize;
      const x1 = Math.cos(angle) * endRadiusPx - halfSize;
      const y1 = Math.sin(angle) * endRadiusPx - halfSize;
      // Calculate delay based on batch index
      const delay = (batchIndex * SPARKLES_CONFIG.BATCH_DELAY_MS) / 1000;
      const duration = durationMs / 1000;
      const rotateAmt =
        SPARKLES_CONFIG.ROTATION_MIN + Math.random() * (SPARKLES_CONFIG.ROTATION_MAX - SPARKLES_CONFIG.ROTATION_MIN);
      return { x0, y0, x1, y1, delay, duration, size, rotateAmt };
    };

    const [params] = useState(generateParams);

    const startX = direction === "in" ? params.x1 : params.x0;
    const startY = direction === "in" ? params.y1 : params.y0;
    const endX = direction === "in" ? params.x0 : params.x1;
    const endY = direction === "in" ? params.y0 : params.y1;
    const delayBatch = direction === "out" ? params.delay : 0;

    return (
      <motion.div
        className="pointer-events-none absolute"
        style={{ left: "50%", top: "50%", zIndex }}
        initial={{ x: startX, y: startY, opacity: 1, scale: 1, rotate: 0 }}
        animate={
          direction === "in"
            ? {
                x: [startX, endX],
                y: [startY, endY],
                opacity: [1, 0.6, 0],
                scale: [1, 0.6, 0.3],
                rotate: [0, params.rotateAmt],
              }
            : { x: [startX, endX], y: [startY, endY], opacity: [1, 0.6, 0], scale: 1, rotate: [0, params.rotateAmt] }
        }
        transition={{ duration: params.duration, delay: delayBatch, ease: "easeOut" }}
      >
        <img
          src={imgStar}
          alt="Star sparkle"
          style={{ width: `${params.size}px`, height: `${params.size}px` }}
        />
      </motion.div>
    );
  };

  // Calculate stars per batch
  const starsPerBatch = Math.ceil(SPARKLES_CONFIG.TOTAL_STARS / SPARKLES_CONFIG.TOTAL_BATCHES);

  return (
    <>
      {Array.from({ length: SPARKLES_CONFIG.TOTAL_BATCHES }).map((_, batchIndex) => {
        const starsInThisBatch = Math.min(starsPerBatch, SPARKLES_CONFIG.TOTAL_STARS - batchIndex * starsPerBatch);

        return Array.from({ length: starsInThisBatch }).map((_, starIndex) => (
          <Sparkle
            key={`${sequenceCycle}-${batchIndex}-${starIndex}`}
            batchIndex={batchIndex}
          />
        ));
      })}
    </>
  );
};

export default SkinnerBoxSparkles;
